import type { IndexedFrame } from '@d2/formats';

export const ATLAS_SIZE = 2048;
export const ATLAS_LAYERS = 12;

/** Where a frame lives in the atlas texture array. */
export interface AtlasSlot {
  layer: number;
  x: number;
  y: number;
  gen: number;
}

/**
 * A drawable palette-indexed image. Upload to the GPU is lazy and transparent: when the atlas
 * fills up it is wiped (generation bump) and sprites re-upload on their next draw.
 */
export class Sprite {
  slot: AtlasSlot | null = null;
  constructor(readonly frame: IndexedFrame) {}
  get width(): number {
    return this.frame.width;
  }
  get height(): number {
    return this.frame.height;
  }
  static of(frames: IndexedFrame[]): Sprite[] {
    return frames.map((f) => new Sprite(f));
  }
}

interface Shelf {
  y: number;
  h: number;
  x: number;
}

/** Shelf-packed R8 texture array. */
export class Atlas {
  readonly texture: WebGLTexture;
  private gen = 1;
  private layer = 0;
  private shelves: Shelf[] = [];
  private nextY = 0;
  uploads = 0;

  constructor(private readonly gl: WebGL2RenderingContext) {
    this.texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.texture);
    gl.texStorage3D(gl.TEXTURE_2D_ARRAY, 1, gl.R8, ATLAS_SIZE, ATLAS_SIZE, ATLAS_LAYERS);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  /** Ensures the sprite is resident; returns false only if it can never fit. */
  ensure(s: Sprite): boolean {
    if (s.slot && s.slot.gen === this.gen) return true;
    const { width: w, height: h } = s.frame;
    if (w === 0 || h === 0) return false;
    if (w > ATLAS_SIZE || h > ATLAS_SIZE) return false;
    let slot = this.place(w + 1, h + 1);
    if (!slot) {
      this.reset();
      slot = this.place(w + 1, h + 1);
      if (!slot) return false;
    }
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texSubImage3D(gl.TEXTURE_2D_ARRAY, 0, slot.x, slot.y, slot.layer, w, h, 1, gl.RED, gl.UNSIGNED_BYTE, s.frame.pixels);
    s.slot = slot;
    this.uploads++;
    return true;
  }

  /** Drops everything; sprites re-upload lazily. Callers must flush pending draws first. */
  reset(): void {
    this.onReset?.();
    this.gen++;
    this.layer = 0;
    this.shelves = [];
    this.nextY = 0;
  }
  onReset?: () => void;

  private place(w: number, h: number): AtlasSlot | null {
    for (;;) {
      for (const sh of this.shelves) {
        if (h <= sh.h && sh.h <= h * 1.5 + 8 && sh.x + w <= ATLAS_SIZE) {
          const slot = { layer: this.layer, x: sh.x, y: sh.y, gen: this.gen };
          sh.x += w;
          return slot;
        }
      }
      if (this.nextY + h <= ATLAS_SIZE) {
        const sh = { y: this.nextY, h, x: 0 };
        this.nextY += h;
        this.shelves.push(sh);
        continue;
      }
      if (this.layer + 1 >= ATLAS_LAYERS) return null;
      this.layer++;
      this.shelves = [];
      this.nextY = 0;
    }
  }
}
